import useInterval from 'beautiful-react-hooks/useInterval';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { ReadyState } from 'react-use-websocket';
import { useWebSocket } from 'react-use-websocket/dist/lib/use-websocket';
import { v4 as uuidV4 } from 'uuid';
import usePrevious from '../../../../Hooks/usePrevious';
import config from '../../../../config';
import {
  DisplayType,
  SessionFieldsFragment, useCreateSessionMutation, useGetLiveSessionQuery,
} from '../../../../generated/core-api/graphql-react-query';
import sendNotification from '../../../../notifications/sendNotification';
import { useUserInfoData } from '../../../Hooks/useUserInfo';
import UseIsWindowVisible from '../useIsWindowVisible';
import useSessionAudio from './useSessionAudio';
import useDetectionNotifications from '../useSession/notificationHooks/useDetectionNotifications';
// import useDurationUpdater from '../useSession/useDurationUpdater';
import useSessionCompliance from '../useSession/useSessionCompliance';
import { SessionComplianceChecklistItem } from '../useSession/useSessionCompliance/type';
import useSessionNavigation from '../useSession/useSessionNavigation';
import { AudioConfig } from '../useAudioConfig';

export interface SessionState {
  readyState: ReadyState;
  startSession: (args: { projectId: string, externalCallId?: string, id?: string }) => void;
  stopSession: () => void;
  sessionLoading: boolean;
  toggleCollapsed: () => void;
  collapsedAt: number | null;
  latestDetection: { id: string, cardId: string, date: number, projectId: string } | null;
  pinnedAt: number | null;
  pin?: () => void;
  unpin?: () => void;
  session?: SessionFieldsFragment | null;
  context: string | null;
  changeContext: (context: string | null) => void;
  compliance: SessionComplianceChecklistItem[];
}

export default function useNewSession(projectId: string | null, audioConfig: AudioConfig, setProjectId: (value: string | null) => void): SessionState {
  const previousProject = usePrevious(projectId);
  const [latestDetection, setLatestDetection] = React.useState<{ id: string, cardId: string, date: number, projectId: string, commandId: string } | null>(null);
  const context = React.useRef<string | null>(null);
  const {
    sendMessage, lastMessage, readyState,
  } = useWebSocket(config.Live.WebSocketUrl, {
    reconnectAttempts: Infinity,
    shouldReconnect: () => true,
    reconnectInterval(lastAttemptNumber) {
      if (lastAttemptNumber < 10) {
        return 500;
      }
      return Math.min(2 ** (lastAttemptNumber - 10) * 1000, 60000);
    },
    onError: () => { console.error('Web socket connection error'); },
  });

  const [collapsedAt, setCollapsedAt] = React.useState<number | null>(null);
  const { isVisible } = UseIsWindowVisible();
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  useDetectionNotifications(latestDetection);

  useInterval(() => {
    sendMessage(JSON.stringify({ type: 'ping' }));
  }, 60000);

  const queryClient = useQueryClient();

  const startMutation = useCreateSessionMutation({
    onSuccess(data) {
      queryClient.setQueryData(
        ['getLiveSession', { input: { id: data.createSession.id } }],
        { getSession: { ...data.createSession, detections: [] } },
      );
    },
    onError(error) {
      console.error('Error creating new session', error);
    },
  });

  const session = useGetLiveSessionQuery({ input: { id: startMutation.data?.createSession.id ?? '' } }, { enabled: !!startMutation.data?.createSession.id });
  const { stopSession: stopWebsocketSession, lastMessage: lastAudioMessage } = useSessionAudio(audioConfig, session.data?.getSession, context.current);
  const compliance = useSessionCompliance(lastAudioMessage, session.data?.getSession, projectId);

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendMessage(JSON.stringify({ cookies: document.cookie, type: 'auth' }));
    }
  }, [readyState]);

  const { pinnedAt, pin, unpin } = useSessionNavigation(latestDetection, session.data?.getSession);

  const stopSession = React.useCallback((endCause: string) => {
    context.current = null;
    if (session) {
      stopWebsocketSession(endCause);
      startMutation.reset();
      setCollapsedAt(null);
      setLatestDetection(null);
      if (session.data?.getSession.project.sessionEndCardId) {
        navigate(`/projects/${session.data?.getSession.projectId}/cards/${session.data?.getSession.project.sessionEndCardId}?displayType=${DisplayType.SessionEndDisplay}`);
      }
    }
  }, [session]);

  const notificationSettings = useUserInfoData().appSettings?.notifications;

  const startSession = React.useCallback((args: { projectId?: string, externalCallId?: string, id?: string }) => {
    const { projectId: sessionProjectId, externalCallId, id } = args;
    if (sessionProjectId && sessionProjectId !== projectId) { setProjectId(sessionProjectId); }
    if (projectId) {
      startMutation.mutate({ input: { projectId: sessionProjectId ?? projectId, externalCallId, id } });
    }
  }, [startMutation]);

  React.useEffect(() => {
    if (session.data?.getSession && projectId && previousProject && previousProject !== projectId) {
      const externalCallId = startMutation.variables?.input.externalCallId;
      stopSession('ProjectChanged');
      startSession({ projectId, externalCallId: externalCallId ?? undefined });
    }
  }, [previousProject, projectId, session.data?.getSession, startMutation.variables?.input.externalCallId, startSession, stopSession]);

  const state = {
    readyState,
    session: session.data?.getSession,
    sessionLoading: startMutation.isLoading,
    startSession,
    stopSession: () => stopSession('ManualSessionTermination'),
    toggleCollapsed() {
      if (collapsedAt) {
        setCollapsedAt(null);
      } else {
        setCollapsedAt(Date.now());
      }
    },
    collapsedAt,
    latestDetection,
    pinnedAt,
    pin,
    unpin,
    context: context.current,
    changeContext(newContext: string | null) { context.current = newContext; },
    compliance,
  };

  React.useEffect(() => {
    if (lastMessage) {
      const data = JSON.parse(lastMessage.data);
      if (data.message) {
        switch (data.message) {
          case 'startSession':
            if (!isVisible && notificationSettings?.notifyOnExternalCall) {
              sendNotification(t('notification.new-session.title'), { body: t('notification.new-session.body') });
            }
            startSession({
              id: uuidV4(),
              projectId: data.session?.projectId,
              externalCallId: data.session?.cogneedId,
            });
            break;

          case 'stopSession':
            stopSession('ExternalCallEnd');
            break;

          case 'OK':
            sendMessage(JSON.stringify({ type: 'ActionTimestamp', message: 'WebNavigation' }));
            break;

          default:
            if (data.type === 'detection') {
              if (data.message.detection) {
                setLatestDetection({
                  id: data.message.detection.id,
                  cardId: data.message.detection.cardId,
                  date: Date.now(),
                  projectId: data.message.detection.projectId,
                  commandId: data.message.detection.commandId,
                });
                if (data.message.detection.contextChange) {
                  context.current = data.message.detection.contextChange;
                }
              }
            }
            break;
        }
      }
    }
  }, [lastMessage]);

  React.useEffect(() => {
    if (lastAudioMessage) {
      const data = JSON.parse(lastAudioMessage.data);
      if (data.message) {
        if (data.type === 'detection') {
          if (data.message.detection) {
            setLatestDetection({
              id: data.message.detection.id,
              cardId: data.message.detection.cardId,
              date: Date.now(),
              projectId: data.message.detection.projectId,
              commandId: data.message.detection.commandId,
            });
            if (data.message.detection.contextChange) {
              context.current = data.message.detection.contextChange;
            }
          }
        }
      }
    }
  }, [lastAudioMessage]);

  return state;
}
